import React from 'react';
import { useState, useEffect } from 'react';

import { withStyles } from '@material-ui/core/styles';
import isEqual from 'react-fast-compare';
import AppBar from '@material-ui/core/AppBar';
import Button from '@material-ui/core/Button';
import DefaultDialog from 'ui/dialog/DefaultDialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

import Typography from '@material-ui/core/Typography';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Toolbar from '@material-ui/core/Toolbar';

import ShareInfo from './forms/ShareInfo';

import EditableLabel from '../../../ui/EditableLabel';

import Recipients from './forms/Recipients';
import AddRecipients from './forms/AddRecipients';

const styles = theme => ({
    appBar: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.secondary.main,
        paddingBottom: '10px',
        position: 'relative',
        '&:after': {
            content: '""',
            borderBottom: `3px solid ${theme.palette.grey['300']}`,
            position: 'absolute',
            bottom: 18,
            left: -24,
            right: -24,
            zIndex: -1,
        },
    },
    appBarTab: {
        '&:hover': {
            backgroundColor: 'transparent',
        },
    },
    dialog: {
        maxWidth: 760,
        height: 'auto',
        minHeight: 0,
    },
    dialogToolbar: { padding: 0 },
    dialogToolbarTabs: { flex: 1 },
    actions: {
        padding: theme.spacing(2),
        justifyContent: 'space-between',
    },
    actionButton: {
        marginRight: theme.spacing(2),

        '&:last-child': {
            marginRight: 0,
        },
    },
});

const MAX_SHARE_NAME_LENGTH = 150;

const shareAlreadyExist = (shares, shareInfo) => {
    return Boolean(
        shares.find(
            share =>
                share.nickname === shareInfo.nickname &&
                share.uuid !== shareInfo.uuid
        )
    );
};

const validate = (shareInfo, shares) => {
    const trimmedNickname = (shareInfo.nickname || '').trim();

    const errors = {
        nickname: trimmedNickname ? '' : 'Required',
    };

    if (!errors.nickname) {
        if (trimmedNickname.length > MAX_SHARE_NAME_LENGTH) {
            errors.nickname = `Invalid: value must not exceed ${MAX_SHARE_NAME_LENGTH} characters`;
        } else if (shareAlreadyExist(shares, shareInfo)) {
            errors.nickname = 'Please enter a unique share name.';
        }
    }

    const valid = Object.values(errors).reduce((acc, val) => {
        if (acc) {
            return !Boolean(val);
        }
        return acc;
    }, true);

    return [valid, errors];
};

const renderShareDialogTitle = (
    shareInfo,
    shareInfoErrors,
    onChange,
    editable
) => {
    if (editable) {
        return (
            <EditableLabel
                fieldInfo={shareInfo}
                fieldInfoErrors={shareInfoErrors}
                fieldName="nickname"
                onChange={(prop, val) => onChange(val)}
            />
        );
    }
    return <Typography variant="h5">{shareInfo.nickname}</Typography>;
};

const getInitialShareState = currentShare => {
    if (currentShare) {
        return {
            uuid: currentShare.uuid,
            nickname: currentShare.nickname,
            owner: currentShare.owner,
            recipient_count: currentShare.recipient_count,
            available_recipients: currentShare.available_recipients,
        };
    }
    return {};
};

let ShareForm = props => {
    const {
        classes,
        open,
        onOk,
        onCancel,
        cancelButtonName = 'Cancel',
        okButtonName = 'Save',
        onDelete,

        currentShare,
        shares,
        editable,
    } = props;

    const [shareChanged, setShareChanged] = useState(false);
    const [tab, setTab] = useState(0);
    const [shareInfo, setShareInfo] = useState(
        getInitialShareState(currentShare)
    );
    const [newOwner, setNewOwner] = useState(null);
    const [recipientsInfo, setRecipientsInfo] = useState(
        currentShare.recipients.map(r => ({ ...r }))
    );

    const [newRecipientsInfo, setNewRecipientsInfo] = useState([]);

    const [shareInfoErrors, setShareInfoErrors] = useState({
        nickname: '',
    });

    useEffect(() => {
        if (
            !isEqual(shareInfo.nickname, currentShare.nickname) ||
            !isEqual(recipientsInfo, currentShare.recipients) ||
            newRecipientsInfo.length > 0 ||
            newOwner
        ) {
            setShareChanged(true);
        } else {
            setShareChanged(false);
        }
    }, [shareInfo.nickname, recipientsInfo, newRecipientsInfo, newOwner]);

    const getAccounts = recipients => {
        return recipients.reduce((acc, item) => {
            if (new Set(['account', 'invitation', 'non-org']).has(item.type)) {
                let res = {
                    account: item.name,
                    permissions: item.permissions,
                    uuid: item.uuid,
                };
                acc.push(res);
            }
            return acc;
        }, []);
    };

    const getGroups = recipients => {
        return recipients.reduce((acc, item) => {
            if (item.type === 'group') {
                acc.push({
                    group: item.name,
                    permissions: item.permissions,
                    uuid: item.uuid,
                });
            }
            return acc;
        }, []);
    };

    const addNewOwner = (accounts, newOwnerEmail) => {
        let found = false;
        let newAccounts = accounts.map(account => {
            if (account.permissions === 'owner') {
                return { ...account, permissions: 'editor' };
            }
            if (account.account === newOwnerEmail) {
                found = true;
                return { ...account, permissions: 'owner' };
            }
            return { ...account };
        });

        if (!found) {
            newAccounts.push({
                account: newOwnerEmail,
                permiossions: 'owner',
            });
        }
        return newAccounts;
    };

    const createPayload = (shareInfo, newOwner) => {
        const allRecipients = [...newRecipientsInfo, ...recipientsInfo];
        const ownerChanged = newOwner && newOwner !== currentShare.owner;
        let accounts = getAccounts(allRecipients);
        if (ownerChanged) {
            accounts = addNewOwner(accounts, newOwner);
        }

        const groups = getGroups(allRecipients);

        const results = {
            uuid: shareInfo.uuid,
            receiver: {
                accounts,
                groups,
            },
        };

        if (shareInfo.nickname !== currentShare.nickname) {
            results.nickname = shareInfo.nickname;
        }

        if (ownerChanged) {
            results.new_owner_email = newOwner;
        }
        return results;
    };

    const handleSave = callback => () => {
        const [valid, errors] = validate(shareInfo, shares);
        setShareInfoErrors(errors);
        if (valid) {
            const results = createPayload(shareInfo, newOwner);
            callback(results);
        }
    };

    const changePermissionsForExistingRecipients = (name, permissions) => {
        setRecipientsInfo(
            recipientsInfo.map(item => {
                if (item.name === name) {
                    item.permissions = permissions;
                }
                return item;
            })
        );
    };

    const removeExistingRecipients = nameToRemove => {
        setRecipientsInfo(
            recipientsInfo.filter(({ name }) => name !== nameToRemove)
        );
    };

    const changePermissionsForNewRecipients = (name, permissions) => {
        setNewRecipientsInfo(
            newRecipientsInfo.map(item => {
                if (item.name === name) {
                    item.permissions = permissions;
                }
                return item;
            })
        );
    };

    const removeNewRecipients = nameToRemove => {
        setNewRecipientsInfo(
            newRecipientsInfo.filter(({ name }) => name !== nameToRemove)
        );
    };

    const changeOwner = data => {
        setNewOwner(data);
    };

    const changeShareName = nickname => {
        setShareInfo({ ...shareInfo, nickname });
    };

    const addNewRecipient = data => {
        setNewRecipientsInfo([...newRecipientsInfo, data]);
    };

    return (
        <DefaultDialog
            open={open}
            onClose={onCancel}
            dialogClass={classes.dialog}
        >
            <DialogTitle>
                {renderShareDialogTitle(
                    shareInfo,
                    shareInfoErrors,
                    changeShareName,
                    editable
                )}
            </DialogTitle>
            <DialogContent style={{ paddingBottom: 5 }}>
                <AppBar
                    position="static"
                    className={classes.appBar}
                    elevation={0}
                >
                    <Toolbar className={classes.dialogToolbar}>
                        <Tabs
                            value={tab}
                            onChange={(event, val) => setTab(val)}
                            textColor="secondary"
                            className={classes.dialogToolbarTabs}
                        >
                            <Tab
                                classes={{ root: classes.appBarTab }}
                                label="Share Info"
                            />
                            <Tab
                                classes={{ root: classes.appBarTab }}
                                label="Recipients"
                            />
                            {editable && (
                                <Tab
                                    classes={{ root: classes.appBarTab }}
                                    label="Add Recipients"
                                />
                            )}
                        </Tabs>
                    </Toolbar>
                </AppBar>
                {tab === 0 && <ShareInfo currentShare={currentShare} />}
                {tab === 1 && (
                    <Recipients
                        editable={editable}
                        recipientsInfo={recipientsInfo}
                        newOwner={newOwner}
                        currentShare={currentShare}
                        onChangeOwner={changeOwner}
                        onChangePermission={
                            changePermissionsForExistingRecipients
                        }
                        onRemoveRecipient={removeExistingRecipients}
                    />
                )}
                {tab === 2 && (
                    <AddRecipients
                        editable={editable}
                        newRecipientsInfo={newRecipientsInfo}
                        currentShare={currentShare}
                        onAddRecipient={addNewRecipient}
                        onChangePermission={changePermissionsForNewRecipients}
                        onRemoveRecipient={removeNewRecipients}
                    />
                )}
            </DialogContent>
            {editable ? (
                <DialogActions className={classes.actions}>
                    <div>
                        {tab === 0 && (
                            <Button onClick={onDelete} color="primary">
                                Delete
                            </Button>
                        )}
                    </div>
                    <div>
                        {onCancel && (
                            <Button
                                onClick={onCancel}
                                className={classes.actionButton}
                                color="primary"
                            >
                                {cancelButtonName}
                            </Button>
                        )}
                        <Button
                            disabled={!shareChanged}
                            onClick={handleSave(onOk)}
                            color="secondary"
                            variant="contained"
                            className={classes.actionButton}
                            autoFocus
                        >
                            {okButtonName}
                        </Button>
                    </div>
                </DialogActions>
            ) : (
                <DialogActions withLeft>
                    <Button
                        onClick={onCancel}
                        variant="contained"
                        className={classes.actionButton}
                        color="secondary"
                    >
                        OK
                    </Button>
                </DialogActions>
            )}
        </DefaultDialog>
    );
};

export default withStyles(styles)(ShareForm);
